import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { CHAT_ACTION_BAR_SWAP_CLASS, CHAT_ACTION_BAR_SWAP_SX } from "./ChatActionBar";

type Props = {
	children: ReactNode;
	/** Layout for the layer itself — how it fills and aligns inside the shared slot. */
	sx?: SxProps<Theme>;
};

/**
 * Content that SHARES a `ChatActionBar`'s slot and yields it while the bar is revealed: at rest this is
 * what the slot shows, on hover/focus the bar takes its place.
 *
 * For a surface too small to give the actions a corner of their own — a calendar day tile whose one spare
 * corner is already carrying the day's number. Swapping beats overlaying: the resting content is never
 * covered by a glyph, and never merely dimmed under one.
 *
 * The host owns the geometry, and that is what makes the swap cost nothing: give the shared slot a fixed
 * width and height and position both layers absolutely inside it, so neither state can resize the slot.
 * Yielding is `opacity` only, so the content keeps its box, its text and its accessible name throughout.
 *
 * Requires `CHAT_ACTION_BAR_REVEAL_SX` on the surrounding surface (it drives both halves of the swap) and
 * `showOnCoarsePointer={false}` on the paired bar, since a bar pinned open on touch would sit on top of
 * this permanently.
 */
export const ChatActionBarSwap: FC<Props> = ({ children, sx }) => (
	<Box className={CHAT_ACTION_BAR_SWAP_CLASS} sx={[CHAT_ACTION_BAR_SWAP_SX, ...(Array.isArray(sx) ? sx : [sx])]}>
		{children}
	</Box>
);
